/* eslint-disable */
/**
 * StandUp Platform Abstraction Layer
 * Triple-Shield implementation for cross-bundler compatibility.
 */

// --- ENVIRONMENT DETECTION ---
export const isDesktop = () => typeof window !== 'undefined' && !!(window as any).__TAURI__;
export const isMobile = () => typeof window !== 'undefined' && !!(window as any).Capacitor?.isNativePlatform?.();
export const isWeb = () => !isDesktop() && !isMobile();

// --- DESKTOP (TAURI) NATIVE ACTIONS ---
export const desktop = {
    invoke: async (command: string, args?: any) => {
        if (!isDesktop()) return null;
        // @ts-ignore
        const { invoke } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/api/core');
        return await invoke(command, args);
    },
    listen: async <T>(event: string, handler: (payload: T) => void) => {
        if (!isDesktop()) return () => {};
        // @ts-ignore
        const { listen } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/api/event');
        return await (listen as any)(event, (e: any) => handler(e.payload));
    },
    openUrl: async (url: string) => {
        if (!isDesktop()) {
            window.open(url, '_blank');
            return;
        }
        try {
            // @ts-ignore
            const { openUrl } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/plugin-opener');
            await openUrl(url);
        } catch (e) {
            window.open(url, '_blank');
        }
    },
    revealItem: async (path: string) => {
        if (!isDesktop()) return;
        // @ts-ignore
        const { revealItemInDir } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/plugin-opener');
        await revealItemInDir(path);
    },
    revealItemInDir: async (path: string) => {
        if (!isDesktop()) return;
        // @ts-ignore
        const { revealItemInDir } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/plugin-opener');
        await revealItemInDir(path);
    },
    getName: async () => {
        if (!isDesktop()) return 'StandUp';
        // @ts-ignore
        const { getName } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/api/app');
        return await getName();
    },
    getVersion: async () => {
        if (!isDesktop()) return '1.0.0';
        // @ts-ignore
        const { getVersion } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/api/app');
        return await getVersion();
    },
    appLogDir: async () => {
        if (!isDesktop()) return '';
        // @ts-ignore
        const { appLogDir } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/api/path');
        return await appLogDir();
    },
    joinPath: async (...parts: string[]) => {
        if (!isDesktop()) return parts.join('/');
        // @ts-ignore
        const { join } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/api/path');
        return await join(...parts);
    },
    setWindowSize: async (width: number, height: number) => {
        if (!isDesktop()) return;
        // @ts-ignore
        const { LogicalSize, getCurrentWindow } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/api/window');
        await getCurrentWindow().setSize(new LogicalSize(width, height));
    },
    relaunch: async () => {
        if (!isDesktop()) return;
        // @ts-ignore
        const { relaunch } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/plugin-process');
        await relaunch();
    },
    checkUpdate: async () => {
        if (!isDesktop()) return null;
        // @ts-ignore
        const { check } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/plugin-updater');
        return await check();
    },
    sendNotification: async (title: string, body: string) => {
        if (!isDesktop()) return;
        // @ts-ignore
        const { isPermissionGranted, requestPermission, sendNotification } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/plugin-notification');
        let permission = await isPermissionGranted();
        if (!permission) {
            permission = (await requestPermission()) === 'granted';
        }
        if (permission) {
            sendNotification({ title, body });
        }
    },
    getWindowLabel: async () => {
        if (!isDesktop()) return null;
        // @ts-ignore
        const { getCurrentWindow } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/api/window');
        return getCurrentWindow().label;
    },
    setupDeepLinks: async (onTokenReceived: (token: string) => void) => {
        if (!isDesktop()) return () => {};
        
        try {
            // @ts-ignore
            const { onOpenUrl } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/plugin-deep-link');
            const unsubPlugin = await (onOpenUrl as any)((urls: string[]) => {
                urls.forEach(urlString => {
                    const url = new URL(urlString);
                    const token = url.searchParams.get('token');
                    if (token) onTokenReceived(token);
                });
            });

            // @ts-ignore
            const { listen } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/api/event');
            const unsubManual = await (listen as any)('deep-link://url', (event: any) => {
                const url = new URL(event.payload);
                const token = url.searchParams.get('token');
                if (token) onTokenReceived(token);
            });

            return () => {
                if (unsubPlugin) unsubPlugin();
                if (unsubManual) unsubManual();
            };
        } catch (e) {
            console.error("Deep link setup failed", e);
            return () => {};
        }
    }
};

// --- MOBILE (CAPACITOR) NATIVE ACTIONS ---
export const mobile = {
    updateStatusBar: async (isDarkMode: boolean) => {
        if (!isMobile()) return;
        try {
            // @ts-ignore
            const { StatusBar, Style } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@capacitor/status-bar');
            await StatusBar.setStyle({ style: isDarkMode ? Style.Dark : Style.Light });
            await StatusBar.setBackgroundColor({ color: isDarkMode ? '#111827' : '#f9fafb' });
        } catch (e) {
            console.error("Status bar update failed", e);
        }
    },
    signInWithGoogle: async () => {
        if (!isMobile()) return null;
        // @ts-ignore
        const { FirebaseAuthentication } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@capacitor-firebase/authentication');
        return await FirebaseAuthentication.signInWithGoogle();
    }
};

// --- SETTINGS CONFIGURATION ---
export const settings = {
    shouldShowProfile: () => !isWeb(), 
    shouldShowShortcuts: () => isDesktop(), 
};
